import axios from "axios";
import { useEffect, useState } from "react";

import { API_URL } from "../constants";
import { authHeader } from "../utils/auth";

/** @param {string} url */
const absoluteMediaUrl = (url) => {
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

/**
 * A media element cannot attach the owner's Bearer header. Fetch the protected
 * MP4 into an in-memory object URL and destroy it whenever its exact preview,
 * report route, or source capability changes.
 * @param {{sourceUrl?: string, active?: boolean, cacheKey?: string}} options
 */
const useAuthenticatedVideoBlob = ({ sourceUrl = "", active = false, cacheKey = "" }) => {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: cacheKey deliberately revokes media when its report/version identity changes.
  useEffect(() => {
    let cancelled = false;
    let currentBlobUrl = "";
    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    setBlobUrl("");
    setError("");

    if (!active || !sourceUrl) {
      setLoading(false);
      return () => controller?.abort();
    }

    setLoading(true);
    axios
      .get(absoluteMediaUrl(sourceUrl), {
        headers: authHeader(),
        responseType: "blob",
        ...(controller ? { signal: controller.signal } : {}),
      })
      .then((response) => {
        if (cancelled) return;
        if (typeof URL.createObjectURL !== "function") {
          throw new Error("Object URLs are not supported in this browser.");
        }
        currentBlobUrl = URL.createObjectURL(response.data);
        setBlobUrl(currentBlobUrl);
      })
      .catch((requestError) => {
        if (cancelled || requestError?.code === "ERR_CANCELED") return;
        setError(
          requestError?.response?.status === 401 || requestError?.response?.status === 403
            ? "Your session can no longer load this private preview. Sign in again, then retry."
            : "The private video could not be loaded. Refresh the page before reviewing it."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller?.abort();
      if (currentBlobUrl && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [active, cacheKey, sourceUrl]);

  return { blobUrl, loading, error };
};

export default useAuthenticatedVideoBlob;
